import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, ChevronRight, FileText, Globe, Plus } from "lucide-react";
import { PrimaryChatMetadata } from "@/shared/models/PrimaryChatMetadata";
import { useChatFeatures } from "@/shared/hooks/useChatFeatures";

interface ResearchContextProps {
  metadata?: PrimaryChatMetadata;
  onAddContext: () => void;
}

export function ResearchContext({
  metadata,
  onAddContext,
}: ResearchContextProps) {
  const [isContextExpanded, setIsContextExpanded] = useState(false);
  const { features } = useChatFeatures();

  return (
    <Card className="mb-4 border-gray-200">
      <div className="flex items-center justify-between p-4">
        <button
          onClick={() => setIsContextExpanded(!isContextExpanded)}
          className="flex-1 flex items-center justify-between hover:bg-gray-50/50 rounded-md p-2"
        >
          <div className="flex items-center gap-2">
            <div className="bg-gray-100 p-1.5 rounded-md">
              <FileText className="h-4 w-4 text-gray-700" />
            </div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">
                Research Context
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>•</span>
                {(() => {
                  const companyCount = metadata?.companyNames.length || 0;
                  return (
                    <span>
                      {companyCount}{" "}
                      {companyCount === 1 ? "Company" : "Companies"}
                    </span>
                  );
                })()}
                <span>•</span>
                {(() => {
                  const sourcesCount = metadata?.sources.length || 0;
                  return (
                    <span>
                      {sourcesCount} {sourcesCount === 1 ? "Source" : "Sources"}
                    </span>
                  );
                })()}
                {metadata?.additionalContext && (
                  <>
                    <span>•</span>
                    <span>Additional Context was added</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <ChevronRight
            className={`h-5 w-5 text-gray-400 transition-transform duration-200
                                      ${isContextExpanded ? "rotate-90" : ""}`}
          />
        </button>

        <Button
          variant="outline"
          size="sm"
          onClick={onAddContext}
          className="ml-4 gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Context
        </Button>
      </div>

      {isContextExpanded && (
        <CardContent className="border-t pt-4">
          <div className="grid grid-cols-2 gap-3">
            {/* Companies */}
            <div className="space-y-1.5">
              <h3 className="text-xs font-medium text-gray-700">Companies</h3>
              <div className="flex flex-wrap gap-1.5">
                {metadata?.companyNames.length ? (
                  metadata.companyNames.map((company) => (
                    <span
                      key={company}
                      className="inline-flex items-center px-2 py-0.5 rounded-full 
                                     text-xs bg-gray-100 text-gray-700 border border-gray-200"
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      {company}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">None specified</span>
                )}
              </div>
            </div>

            {/* Source */}
            <div className="space-y-1.5">
              <h3 className="text-xs font-medium text-gray-700">Documents</h3>
              <div className="flex flex-wrap gap-1.5">
                {metadata?.sources.length ? (
                  <>
                    {metadata?.sources.map((source) => (
                      <span
                        key={source}
                        className="inline-flex items-center px-2 py-0.5 rounded-full 
                                       text-xs bg-gray-100 text-gray-700 border border-gray-200"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        {source}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="text-xs text-gray-500">
                    No sources selected
                  </span>
                )}
              </div>
            </div>

            {/* Additional Context */}
            {metadata?.additionalContext && (
              <div className="col-span-2 space-y-1.5 mt-1.5">
                <h3 className="text-xs font-medium text-gray-700">
                  Additional Context
                </h3>
                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded-md border">
                  {metadata.additionalContext}
                </div>
              </div>
            )}

            {/* Web Access */}
            {features.webSearchEnabled && (
              <div className="col-span-2 flex gap-3 mt-1.5">
                {features.webSearchEnabled && (
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`p-1 rounded-md ${
                        metadata?.useInternet
                          ? "bg-gray-900 text-white"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      <Globe className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-xs text-gray-700">
                      Web and News Access:{" "}
                      <span className="font-medium">
                        {metadata?.useInternet ? "Yes" : "No"}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
